// src/pages/FormEdit.jsx
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUserData } from "../redux/slices/userDataSlice";
import FormPreview from '../components/form-builder/FormPreview';
import FormArea from '../components/form-builder/FormArea';
import SideMenu from '../components/form-builder/SideMenu';
import Settings from './Settings';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const FormEdit = () => {
  const { t } = useTranslation();
  const { formId } = useParams();
  const navigate = useNavigate();
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [shareDialog, setShareDialog] = useState(false);
  const [formLink, setFormLink] = useState('');
  const [calculationValues, setCalculationValues] = useState({});
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDirty, setIsDirty] = useState(false);

  // Sayfa hesaplamaları
  const maxPage = Math.max(...(formData?.fields || []).map(f => f.page || 1), 1);
  const currentFields = (formData?.fields || []).filter(f => (f.page || 1) === currentPage);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`https://app.hhoms.com/api/form/Libraries/${formId}`, {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
          },
        });

        if (response.data.formLibraries && response.data.formLibraries[0]) {
          const formContent = response.data.formLibraries[0].formContent;
          const parsedData = JSON.parse(formContent);

          // Hesaplama alanı değerlerini çıkar
          const calculationValues = {};
          parsedData.fields.forEach(field => {
            if (field.type === 'calculation' && field.calculationData) {
              calculationValues[field.id] = field.calculationData;
            }
          });

          setFormData(parsedData);
          setCalculationValues(calculationValues);
        } else {
          setError('Form bulunamadı veya yayınlanmamış');
        }
      } catch (err) {
        setError('Form yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [formId, userData.id]);

  const handleCalculationChange = (fieldId, value) => {
    setCalculationValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    setIsDirty(true);
  };

  const handleSaveForm = async () => {
    try {
      // Hesaplama alanı değerlerini form alanlarına ekle
      const updatedFields = formData.fields.map(field => {
        if (field.type === 'calculation') {
          return {
            ...field,
            calculationData: calculationValues[field.id]
          };
        }
        return field;
      });

      const content = {
        formId: formId,
        title: formData.title,
        formContent: {
          ...formData,
          fields: updatedFields
        },
      };

      await axios.put(`https://app.hhoms.com/api/form/Libraries`, content, {
        headers: {
          'Content-Type': 'application/json',
          ApplicationToken: appToken,
          User: userData.id,
        },
      });
      setSnackbar({ open: true, message: t('common.form_updated_successfully'), severity: 'success' });
      setIsDirty(false);
      navigate(`/form/${formId}`);
    } catch (error) {
      setSnackbar({ open: true, message: t('common.error_updating_form'), severity: 'error' });
    }
  };

  const handlePublish = async () => {
    try {
      setSnackbar({ open: true, message: t('common.form_published_successfully'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: t('common.error_publishing_form'), severity: 'error' });
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleAddPage = () => {
    const newPage = maxPage + 1;
    setCurrentPage(newPage);
    setIsDirty(true);
    setSnackbar({ open: true, message: t('common.page_added'), severity: 'success' });
  };

  const handleDeletePage = (pageToDelete) => {
    if (maxPage <= 1) {
      setSnackbar({ open: true, message: t('common.cannot_delete_last_page'), severity: 'warning' });
      return;
    }

    const updatedFields = formData.fields
      .filter(field => (field.page || 1) !== pageToDelete)
      .map(field => {
        const fieldPage = field.page || 1;
        if (fieldPage > pageToDelete) {
          return { ...field, page: fieldPage - 1 };
        }
        return field;
      });

    setFormData(prev => ({ ...prev, fields: updatedFields }));

    if (currentPage === pageToDelete) {
      setCurrentPage(Math.min(currentPage, maxPage - 1));
    } else if (currentPage > pageToDelete) {
      setCurrentPage(currentPage - 1);
    }

    setIsDirty(true);
    setSnackbar({ open: true, message: t('Sayfa Sil'), severity: 'success' });
  };

  const handleFieldUpdate = (fieldId, updates) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    }));
    setIsDirty(true);
  };

  const handleFieldDelete = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.id !== fieldId)
    }));
    setIsDirty(true);
  };

  const handleAddField = (fieldType, page = currentPage) => {
    // Alan tiplerine göre Türkçe isimler
    const fieldLabels = {
      text: 'Metin Alanı',
      textarea: 'Çok Satırlı Metin',
      number: 'Sayı Alanı',
      email: 'E-posta Adresi',
      phone: 'Telefon Numarası',
      date: 'Tarih Seçici',
      time: 'Saat Seçici',
      select: 'Açılır Menü',
      radio: 'Seçim Butonu',
      checkbox: 'Çoklu Seçim',
      file: 'Dosya Yükleme',
      image: 'Resim Yükleme',
      pdf: 'PDF Yükleme',
      signature: 'İmza Alanı',
      table: 'Tablo',
      calculation: 'Hesaplama Alanı',
      address: 'Adres Bilgisi',
      name: 'Ad Soyad'
    };

    console.log(fieldType)
    var newField = {};
    if (typeof fieldType !== 'string') {
      newField = {
        id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: fieldType.type,
        label: fieldLabels[fieldType.type] || `${fieldType.type.charAt(0).toUpperCase() + fieldType.type.slice(1)} Alanı`,
        required: false,
        page: page
      };
    } else {
      newField = {
        id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: fieldType,
        label: fieldLabels[fieldType] || `${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} Alanı`,
        required: false,
        page: page
      };
    }

    // Add type-specific properties
    switch (fieldType) {
      case 'select':
        newField.options = ['Seçenek 1', 'Seçenek 2', 'Seçenek 3'];
        break;
      case 'radio':
        newField.options = ['Seçenek 1', 'Seçenek 2'];
        break;
      case 'checkbox':
        newField.options = ['Seçenek 1', 'Seçenek 2', 'Seçenek 3'];
        break;
      case 'number':
        newField.min = 0;
        newField.max = 100;
        newField.placeholder = 'Sayı giriniz';
        break;
      case 'text':
        newField.placeholder = 'Metin giriniz';
        break;
      case 'textarea':
        newField.placeholder = 'Açıklama yazınız';
        newField.rows = 3;
        break;
      case 'email':
        newField.placeholder = 'ornek@email.com';
        break;
      case 'phone':
        newField.placeholder = '+90 5XX XXX XX XX';
        break;
      case 'table':
        newField.columns = [
          { header: 'Sütun 1', type: 'text', width: '40%' },
          { header: 'Sütun 2', type: 'number', width: '30%' },
          { header: 'Sütun 3', type: 'date', width: '30%' }
        ];
        newField.minRows = 1;
        newField.maxRows = 10;
        break;
      default:
        break;
    }

    setFormData(prev => ({
      ...prev,
      fields: [...(prev.fields || []), newField]
    }));
    setIsDirty(true);

    console.log('Added field:', newField);
    setSnackbar({
      open: true,
      message: `${fieldLabels[fieldType] || 'Alan'} başarıyla eklendi`,
      severity: 'success'
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const fieldType = e.dataTransfer.getData('fieldType');
    if (fieldType) {
      handleAddField(fieldType, currentPage);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#f8fafc'
    }}>
      <AppBar position="static" color="default" elevation={0} sx={{
        bgcolor: 'white',
        borderBottom: '1px solid #e2e8f0',
        '& .MuiToolbar-root': {
          minHeight: '64px !important',
        }
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => navigate('/')}
            sx={{
              mr: 2,
              color: '#64748b',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                color: '#1976d2'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{
            ml: 2,
            flexGrow: 1,
            fontWeight: 600,
            color: '#1e293b',
            letterSpacing: '-0.5px'
          }}>
            {formData?.title || t('common.form_editor')}
          </Typography>

          {/* Preview Button */}
          <Button
            startIcon={<DescriptionIcon />}
            variant="outlined"
            onClick={() => setPreviewModalOpen(true)}
            sx={{
              mr: 2,
              color: '#64748b',
              borderColor: '#e2e8f0',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              '&:hover': {
                borderColor: '#1976d2',
                color: '#1976d2',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.1)'
              }
            }}
          >
            {t('Önizle')}
          </Button>

          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={handleSaveForm}
            disabled={!isDirty}
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
              },
              '&.Mui-disabled': {
                background: '#e2e8f0',
                color: '#9ca3af',
                boxShadow: 'none'
              }
            }}
          >
            {t('Kaydet')}
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        mx: 0,
        mb: 0,
        bgcolor: 'transparent'
      }}>
        <SideMenu
          currentPage={currentPage}
          onAddField={(fieldType) => {
            console.log('🎯 SideMenu onAddField çağrıldı:', fieldType, 'currentPage:', currentPage);
            handleAddField(fieldType, currentPage);
          }}
        />
        <Box
          sx={{
            flex: 1,
            p: 3,
            bgcolor: '#f8fafc',
            overflowY: 'auto',
            borderLeft: '1px solid #e2e8f0'
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {/* Form Başlık ve Açıklama */}
          <Paper sx={{
            p: 3,
            mb: 3,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            bgcolor: 'white'
          }}>

            <TextField
              fullWidth
              label={t('Form Başlığı')}
              value={formData?.title || ''}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                setIsDirty(true);
              }}
              variant="outlined"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2'
                  }
                }
              }}
            />
            <TextField
              fullWidth
              label={t('Açıklama')}
              value={formData?.description || ''}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                setIsDirty(true);
              }}
              variant="outlined"
              multiline
              rows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2'
                  }
                }
              }}
            />
          </Paper>

          {/* Form Alanları */}
          <Paper sx={{
            minHeight: 400,
            border: '2px dashed #cbd5e1',
            borderRadius: 2,
            p: 3,
            bgcolor: '#f8fafc',
            position: 'relative',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#1976d2',
              bgcolor: '#f0f9ff'
            }
          }}>

            <Box sx={{ mt: 4 }}>
              <FormArea
                formFields={currentFields}
                onRemoveField={handleFieldDelete}
                onUpdateField={handleFieldUpdate}
                calculationValues={calculationValues}
                onCalculationChange={handleCalculationChange}
                currentPage={currentPage}
                onAddField={(fieldType) => handleAddField(fieldType, currentPage)}
              />
              {currentFields.length === 0 && (
                <Box sx={{
                  height: '320px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}>

                </Box>
              )}
            </Box>

            {/* Sayfa Yönetimi Butonları - Form alanlarının altında (FormBuilder'daki gibi) */}
            <Paper sx={{
              p: 2,
              mt: 2,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>

                <Chip
                  label={`${currentFields.length} ${t('pages')}`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.round((currentPage / maxPage) * 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)'
                    }
                  }}
                />
                <Typography align="center" sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
                  {`${t('pages')} ${currentPage} / ${maxPage}`}
                </Typography>
              </Box>

              {/* Sayfa Yönetimi Butonları */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}>
                <Button
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    minWidth: 100,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)'
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.5)'
                    }
                  }}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  {t('Geri Git')}
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      bgcolor: 'rgba(76, 175, 80, 0.9)',
                      color: 'white',
                      fontWeight: 600,
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'rgba(76, 175, 80, 1)'
                      }
                    }}
                    onClick={handleAddPage}
                  >
                    {t('Sayfa Ekle')}
                  </Button>

                  {maxPage > 1 && (
                    <Tooltip title={t('Sayfa Sil')} placement="top">
                      <IconButton
                        onClick={() => handleDeletePage(currentPage)}
                        sx={{
                          bgcolor: 'rgba(244, 67, 54, 0.9)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(244, 67, 54, 1)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    minWidth: 100,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)'
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.5)'
                    }
                  }}
                  onClick={() => setCurrentPage(p => Math.min(maxPage, p + 1))}
                  disabled={currentPage === maxPage}
                >
                  {t('İleri Git')}
                </Button>
              </Box>
            </Paper>
          </Paper>
        </Box>
      </Box>

      {/* Preview Modal */}
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '95vh',
            bgcolor: '#f8fafc'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 2
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>

            <IconButton
              onClick={() => setPreviewModalOpen(false)}
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          p: 0,
          bgcolor: '#f8fafc'
        }}>
          <Box sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            minHeight: 'calc(80vh - 120px)',
            overflow: 'auto'
          }}>
            <Box sx={{
              width: '100%',
              maxWidth: '800px',
              transition: 'width 0.3s ease',
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.02), 0 10px 15px -3px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <FormPreview
                formFields={formData?.fields || []}
                formTitle={formData?.title}
                formDescription={formData?.description}
                showSubmitButton={false}
              />
            </Box>
          </Box>
        </DialogContent>

      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={shareDialog}
        onClose={() => setShareDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('common.form_link')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={formLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={() => navigator.clipboard.writeText(formLink)}>
                  <ContentCopyIcon />
                </IconButton>
              ),
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '& .MuiAlert-icon': {
              alignItems: 'center'
            },
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormEdit;